import React from 'react';
import { motion } from 'framer-motion';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Building2, Zap, Crown, Check, ChevronRight, Loader2 } from 'lucide-react';

const PLAN_ICONS = { starter: Zap, standard: Crown, enterprise: Building2 };
const PLAN_COLORS = {
  starter: 'from-blue-500 to-cyan-500',
  standard: 'from-purple-500 to-indigo-500',
  enterprise: 'from-amber-500 to-orange-500',
};

export const PlansView = ({ plans, regForm, setRegForm, onRegister, registering }) => (
  <div className="space-y-8" data-testid="campus-plans">
    <div className="text-center">
      <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[var(--isp-heading)] tracking-tight">
        Campus Plans
      </h1>
      <p className="text-[var(--isp-body)] mt-3 text-base sm:text-lg max-w-2xl mx-auto">
        Empower your institution with AI-powered learning tools for every student
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {plans.map((plan, i) => {
        const Icon = PLAN_ICONS[plan.id] || Building2;
        const gradient = PLAN_COLORS[plan.id] || 'from-purple-500 to-indigo-500';
        const isPopular = plan.id === 'standard';
        const ringCls = isPopular ? 'ring-2 ring-purple-500/50' : '';
        return (
          <motion.div
            key={plan.id}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
          >
            <Card
              className={`p-6  bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)] relative ${ringCls}`}
              data-testid={`plan-${plan.id}`}
            >
              {isPopular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-purple-500 text-white text-xs font-medium">
                  Most Popular
                </div>
              )}
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center mb-4`}>
                <Icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white">{plan.name}</h3>
              <p className="text-sm text-[var(--isp-body)] mt-1">{plan.description}</p>
              <div className="mt-4">
                <span className="text-3xl font-bold text-white">${plan.price_per_user_monthly}</span>
                <span className="text-[var(--isp-body)] text-sm"> /user/mo</span>
              </div>
              <p className="text-xs text-[var(--isp-muted)] mt-1">Up to {plan.max_users.toLocaleString()} users</p>
              <div className="mt-6 space-y-2">
                {plan.features.map((f, fi) => (
                  <div key={fi} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                    <span className="text-sm text-[var(--isp-body)]">{f}</span>
                  </div>
                ))}
              </div>
              <Button
                onClick={() => setRegForm({ ...regForm, plan_id: plan.id })}
                className={`w-full mt-6 rounded-xl gap-2 ${isPopular ? 'bg-gradient-to-r from-purple-500 to-indigo-500' : 'bg-slate-700 hover:bg-slate-600'}`}
                data-testid={`select-plan-${plan.id}`}
              >
                Get Started <ChevronRight className="w-4 h-4" />
              </Button>
            </Card>
          </motion.div>
        );
      })}
    </div>

    {regForm.plan_id && (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <Card
          className="p-6  bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)] max-w-lg mx-auto"
          data-testid="campus-register-form"
        >
          <h3 className="text-xl font-semibold text-[var(--isp-heading)] mb-4">Register Your Campus</h3>
          <div className="space-y-4">
            <input
              placeholder="Campus/Department Name"
              value={regForm.name}
              onChange={e => setRegForm({ ...regForm, name: e.target.value })}
              className="w-full p-3 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-white text-sm"
              data-testid="reg-name"
            />
            <input
              placeholder="Institution Name"
              value={regForm.institution}
              onChange={e => setRegForm({ ...regForm, institution: e.target.value })}
              className="w-full p-3 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-white text-sm"
              data-testid="reg-institution"
            />
            <input
              placeholder="Admin Email"
              value={regForm.admin_email}
              onChange={e => setRegForm({ ...regForm, admin_email: e.target.value })}
              className="w-full p-3 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-white text-sm"
              data-testid="reg-email"
            />
            <div className="flex gap-3">
              <Button
                onClick={onRegister}
                disabled={registering}
                className="flex-1 rounded-xl gap-2 bg-gradient-to-r from-purple-500 to-indigo-500"
                data-testid="register-campus-btn"
              >
                {registering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Building2 className="w-4 h-4" />}
                Register Campus
              </Button>
              <Button
                onClick={() => setRegForm({ ...regForm, plan_id: '' })}
                variant="outline"
                className="rounded-xl border-[var(--isp-glass-border)] text-[var(--isp-body)]"
              >
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      </motion.div>
    )}
  </div>
);
